import { arrayMove } from '@dnd-kit/sortable'
import { Button, Divider, Flex, Modal, Space } from 'antd'
import Title from 'antd/es/typography/Title'
import dayjs, { Dayjs } from 'dayjs'
import { FC, useEffect, useMemo, useState } from 'react'
import { getUserFirstName } from '../../../../helpers/get-user-first-name'
import { useUser } from '../../../../hooks/use-user'
import { useGetFormOrderByDocIdQuery } from '../../../../store/api/form-orders-api'
import {
    useGetFormSetByDocIdQuery,
    useUpdateFormSetMutation,
} from '../../../../store/api/form-sets-api'
import Form from '../../../../types/api/form'
import FormSet from '../../../../types/api/form-set'
import Task from '../../../../types/api/task'
import { getFormOrderDetailsFields } from '../../helpers/form-order-details'
import { CreationForm } from './components/form-set-editor/components/creation-form/creation-form'
import { FormsTable } from './components/form-set-editor/components/forms-table/forms-table'
import { FormsOrderEditor } from './components/forms-order-editor/forms-order-editor'
import SavedOrderNumbers from './components/saved-order-numbers'
import { buildExtendedOrderNum, buildFormNum } from './helpers/build-form-num'
import { NumberingMode } from './types/order-draft'
import { SavedFormOrder, useSaveFormOrder } from './use-save-form-order'

const EMPTY_FORMS: Form[] = []

interface FormsSetOrdersEditorProps {
    task: Task
    formSet?: FormSet
    onSuccess: () => void
}

/**
 * Два слоя:
 * 1. Набор форм — сразу на сервер (CreationForm / таблица набора). Порядок пластин — DnD в таблице набора, сохраняется в FormSet.
 * 2. Черновик заказа — только в памяти, пока открыта модалка; порядок строк = порядок набора.
 *
 * Кнопки: «Сохранить заказ» (Form Order + новые Form) → номера для копирования
 * → «Завершить задачу». После save модалку можно закрыть и открыть снова.
 */
export const FormsSetOrdersEditor: FC<FormsSetOrdersEditorProps> = ({
    task,
    formSet: initialFormSet,
    onSuccess,
}) => {
    const [formSet, setFormSet] = useState(initialFormSet)
    // Состав черновика — id форм из набора (множество); порядок задаёт formsOrderIds
    const [draftIds, setDraftIds] = useState<string[]>([])
    const [formsOrderIds, setFormsOrderIds] = useState<string[]>([])
    const [keepNumByFormId, setKeepNumByFormId] = useState<Record<string, boolean>>({})
    const [numberingMode, setNumberingMode] = useState<NumberingMode>('new')
    const [estimatedDate, setEstimatedDate] = useState<Dayjs | null>(null)
    const [supplierDocId, setSupplierDocId] = useState<string | undefined>()
    const [savedOrder, setSavedOrder] = useState<SavedFormOrder | null>(null)
    // После save источники A уходят из набора — таблица черновика держит снимок строк
    const [lockedDraftForms, setLockedDraftForms] = useState<Form[]>([])

    const currentUser = useUser()
    const { save, complete, isSaving, isCompleting } = useSaveFormOrder()
    const [updateFormSet] = useUpdateFormSetMutation()
    const details = getFormOrderDetailsFields(task)

    // Повторное открытие после save: заказ уже в details, черновика нет
    const existingOrderId = details.formOrderId

    const { data: existingOrderResponse } = useGetFormOrderByDocIdQuery(
        [existingOrderId || '', { populate: ['forms', 'supplier'] }],
        { skip: !existingOrderId || !!savedOrder }
    )

    const { data: formSetResponse } = useGetFormSetByDocIdQuery(
        [formSet?.documentId || '', { populate: ['forms', 'forms.form_order'] }],
        { skip: !formSet?.documentId }
    )

    const formsFromApi = formSetResponse?.data.forms ?? EMPTY_FORMS

    // Локальный порядок набора: сохраняем DnD, подтягиваем новые id с API в конец
    useEffect(() => {
        const apiIds = formsFromApi.map(f => f.documentId)
        setFormsOrderIds(prev => {
            const kept = prev.filter(id => apiIds.includes(id))
            const added = apiIds.filter(id => !kept.includes(id))
            const next = [...kept, ...added]
            if (next.length === prev.length && next.every((id, i) => id === prev[i])) return prev
            return next
        })
    }, [formsFromApi])

    const forms = useMemo(
        () =>
            formsOrderIds
                .map(id => formsFromApi.find(f => f.documentId === id))
                .filter((f): f is Form => !!f),
        [formsFromApi, formsOrderIds]
    )

    // Черновик в порядке набора (не в порядке добавления в draftIds)
    const draftForms = useMemo(
        () => forms.filter(f => draftIds.includes(f.documentId)),
        [forms, draftIds]
    )

    // Перезаказ: в details уже есть sourceFormIds — кладём эти пластины в черновик,
    // если заказ ещё не сохранён и формы есть в текущем наборе.
    useEffect(() => {
        if (existingOrderId || savedOrder) return
        const sourceFormIds = details.sourceFormIds
        if (!sourceFormIds?.length || !formsFromApi.length) return

        const idsInSet = sourceFormIds.filter(id =>
            formsFromApi.some(form => form.documentId === id)
        )
        if (!idsInSet.length) return

        setDraftIds(prev => (prev.length ? prev : idsInSet))
    }, [existingOrderId, savedOrder, formsFromApi, details.sourceFormIds])

    // Повторное открытие модалки после save: заказ уже на сервере (formOrderId в details).
    // Поднимаем savedOrder, дату и снимок строк, чтобы редактор открылся read-only.
    useEffect(() => {
        const order = existingOrderResponse?.data
        if (!order || !formSet || savedOrder) return

        const formNum = buildFormNum(formSet.id, order.id)
        setSavedOrder({
            documentId: order.documentId,
            id: order.id,
            formNum,
            estimatedDate: order.estimatedDate ? dayjs(order.estimatedDate) : dayjs(),
            hasNewFormNums: !!order.forms?.some(f => f.num === formNum),
        })
        if (order.estimatedDate) setEstimatedDate(dayjs(order.estimatedDate))
        if (order.supplier?.documentId) setSupplierDocId(order.supplier.documentId)
        if (order.forms?.length) setLockedDraftForms(order.forms)
    }, [existingOrderResponse, formSet, savedOrder])

    const isSaved = !!savedOrder
    const orderTableForms = isSaved ? lockedDraftForms : draftForms

    const canSave =
        !!formSet?.documentId && draftIds.length > 0 && estimatedDate != null && !isSaved && !!supplierDocId

    const savedNums = useMemo(() => {
        if (!savedOrder || !estimatedDate) return null
        return {
            formNum: savedOrder.formNum,
            extendedNum: buildExtendedOrderNum(
                savedOrder.formNum,
                getUserFirstName(currentUser?.username),
                estimatedDate
            ),
        }
    }, [savedOrder, estimatedDate, currentUser?.username])

    const addToDraft = (formDocumentId: string) => {
        if (isSaved) return
        setDraftIds(ids => (ids.includes(formDocumentId) ? ids : [...ids, formDocumentId]))
    }

    const addAllToDraft = () => {
        if (isSaved) return
        setDraftIds(ids => {
            const next = new Set(ids)
            forms.forEach(f => next.add(f.documentId))
            return [...next]
        })
    }

    const removeFromDraft = (formDocumentId: string) => {
        if (isSaved) return
        setDraftIds(ids => ids.filter(id => id !== formDocumentId))
    }

    const reorderForms = async (activeId: string, overId: string) => {
        if (isSaved || !formSet?.documentId) return

        const oldIndex = formsOrderIds.indexOf(activeId)
        const newIndex = formsOrderIds.indexOf(overId)
        if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return

        const previousIds = formsOrderIds
        const nextIds = arrayMove(formsOrderIds, oldIndex, newIndex)
        setFormsOrderIds(nextIds)

        try {
            await updateFormSet({
                docId: formSet.documentId,
                body: {
                    data: {
                        forms: { set: nextIds },
                    },
                },
            }).unwrap()
        } catch {
            setFormsOrderIds(previousIds)
            Modal.error({ title: 'Не удалось сохранить порядок форм в наборе' })
        }
    }

    const onSave = async () => {
        const stampId = task.shipment?.stamp?.documentId || details.stampId

        if (!stampId) {
            Modal.error({ title: 'Не найден stampId для задачи' })
            return
        }
        if (!formSet || !estimatedDate) return

        // Снимок до save: источники исчезнут из набора после disconnect
        setLockedDraftForms(draftForms)

        const result = await save({
            task,
            formSet,
            sourceForms: draftForms,
            numberingMode,
            keepNumByFormId,
            estimatedDate,
            supplierDocId,
            stampId,
            shipment: task.shipment,
        })

        if (result) setSavedOrder(result)
    }

    const onComplete = async () => {
        if (!savedOrder) return
        const ok = await complete(task, savedOrder)
        if (ok) onSuccess()
    }

    return (
        <>
            <Title level={3}>Набор форм № {formSet?.id || ' - '}</Title>
            <Space style={{ width: '100%' }} direction='vertical' size={16}>
                {formSet?.documentId ? (
                    <FormsTable
                        formSetDocId={formSet.documentId}
                        forms={forms}
                        draftIds={draftIds}
                        orderSaved={isSaved}
                        onAddToDraft={addToDraft}
                        onReorderForms={isSaved ? undefined : reorderForms}
                        onAddAllToDraft={addAllToDraft}
                    />
                ) : null}
                <CreationForm formSet={formSet} onCreatedFormSet={setFormSet} />
            </Space>

            <Divider />

            <FormsOrderEditor
                orderId={savedOrder?.id || 0}
                numberingMode={numberingMode}
                draftForms={orderTableForms}
                keepNumByFormId={keepNumByFormId}
                supplierDocId={supplierDocId}
                estimatedDate={estimatedDate}
                assignedNum={savedOrder?.formNum}
                readOnly={isSaved}
                onNumberingModeChange={setNumberingMode}
                onToggleKeepNum={(id, keep) =>
                    setKeepNumByFormId(prev => ({ ...prev, [id]: keep }))
                }
                onRemoveFromDraft={removeFromDraft}
                onSupplierChange={setSupplierDocId}
                onEstimatedDateChange={setEstimatedDate}
            />

            <Flex justify='space-between' align='center' style={{ marginTop: 24 }} gap={24}>
                {savedNums && savedOrder?.hasNewFormNums ? (
                    <SavedOrderNumbers
                        formNum={savedNums.formNum}
                        extendedNum={savedNums.extendedNum}
                    />
                ) : (
                    <span />
                )}
                <Space>
                    {!isSaved ? (
                        <Button
                            type='primary'
                            disabled={!canSave}
                            loading={isSaving}
                            onClick={onSave}
                        >
                            Сохранить заказ
                        </Button>
                    ) : (
                        <Button type='primary' loading={isCompleting} onClick={onComplete}>
                            Завершить задачу
                        </Button>
                    )}
                </Space>
            </Flex>
        </>
    )
}
